import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useViewport } from "../../contexts";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import type { ILocationDetail, LocationUpdatePayload } from "../../customTypings/location/Location";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import { useGoogleMapsConfig } from "@/hooks/location/useGoogleMapsConfig";
import { locationsService } from "../../services/locations/locations.service";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    mapAddressLine1Validation,
    mapCoordinateField,
    mapCountryValidation,
    optionalNullableString,
    arePayloadsEqual,
    EntityLabels,
    hydratePostalAddressForForm,
    isUnchangedUpdateResponse,
    mapPostalAddressToApi,
    NO_CHANGES_TOAST_MESSAGE,
    showToastError,
    showToastSuccess,
    showToastUnchanged,
} from "../../utils";
import { formatLocationToastLabel } from "../LocationsListPage/locationAdminListActions";
import { LocationEditFormGrid, type LocationFormValues } from "./LocationEditFormGrid";
import { useLocationEditOutletContext } from "./locationEditOutletContext";

const validationSchema = Yup.object({
    id: Yup.string().optional(),
    name: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Name is required.")
        .max(255, "Name must be at most 255 characters."),
    addressLine1: mapAddressLine1Validation,
    addressLine2: optionalNullableString,
    addressLine3: optionalNullableString,
    town: optionalNullableString,
    county: optionalNullableString,
    postcode: optionalNullableString,
    country: mapCountryValidation,
    latitude: mapCoordinateField,
    longitude: mapCoordinateField,
    isEnabled: Yup.boolean().required(),
});

const emptyForm: LocationFormValues = {
    name: "",
    ...hydratePostalAddressForForm({}),
    latitude: null,
    longitude: null,
    isEnabled: true,
};

const LocationEditFormView = () => {
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const { locationDetail, metaDeleted, metaEnabled, setConfirmKind } =
        useLocationEditOutletContext();
    const { config: mapConfig, loading: mapConfigLoading, error: mapConfigError } = useGoogleMapsConfig();

    const mapsEnabled =
        mapConfig?.enabled === true && Boolean(mapConfig.browserApiKey) && Boolean(mapConfig.mapId);

    const [initialValues, setInitialValues] = useState<LocationFormValues>(emptyForm);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const savedUpdatePayloadRef = useRef<LocationUpdatePayload | null>(null);
    const leaveAfterSaveRef = useRef(false);
    const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);

    const formAutoFocusRef = useFormAutoFocus(!mapConfigLoading && !!locationDetail, [mapConfigLoading, locationDetail]);

    const buildUpdatePayload = (values: LocationFormValues): LocationUpdatePayload => ({
        id: values.id!,
        name: values.name.trim(),
        latitude: values.latitude!,
        longitude: values.longitude!,
        ...mapPostalAddressToApi(values),
    });

    const buildUpdatePayloadFromDetail = (location: ILocationDetail): LocationUpdatePayload =>
        buildUpdatePayload({
            id: location.id,
            name: location.name,
            ...hydratePostalAddressForForm(location),
            latitude: location.latitude ?? null,
            longitude: location.longitude ?? null,
            isEnabled: location.isEnabled,
        });

    useEffect(() => {
        if (!locationDetail) return;
        setInitialValues({
            id: locationDetail.id,
            name: locationDetail.name,
            ...hydratePostalAddressForForm(locationDetail),
            latitude: locationDetail.latitude ?? null,
            longitude: locationDetail.longitude ?? null,
            isEnabled: locationDetail.isEnabled,
        });
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(locationDetail);
    }, [locationDetail]);

    if (mapConfigLoading || !locationDetail) {
        return (
            <div className="my-4 d-flex justify-content-center">
                <LoadingSpinner text="Loading location…" />
            </div>
        );
    }

    return (
        <>
            {mapConfigError && (
                <Alert variant="danger" className="mb-3">
                    {mapConfigError}
                </Alert>
            )}

            {!mapsEnabled && (
                <Alert variant="warning" className="mb-3">
                    Maps are not set up for this system. You cannot use the map or address search until an administrator enables maps.
                    {" "}You can still edit and save existing coordinates loaded from the record.
                </Alert>
            )}

            <Formik<LocationFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur={false}
                onSubmit={async (values, { resetForm }) => {
                    if (metaDeleted) return;
                    saveOutcomeRef.current = null;
                    setLiveValidationAfterSubmit(true);
                    try {
                        if (!values.id) return;
                        const updatePayload = buildUpdatePayload(values);
                        if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                            setLiveValidationAfterSubmit(false);
                            saveOutcomeRef.current = "unchanged";
                            showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                            if (leaveAfterSaveRef.current) return;
                            return;
                        }
                        const result = await locationsService.update(updatePayload);
                        if (isUnchangedUpdateResponse(result)) {
                            setLiveValidationAfterSubmit(false);
                            saveOutcomeRef.current = "unchanged";
                            showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                            if (leaveAfterSaveRef.current) return;
                            return;
                        }
                        setLiveValidationAfterSubmit(false);
                        saveOutcomeRef.current = "success";
                        savedUpdatePayloadRef.current = updatePayload;
                        showToastSuccess(`${formatLocationToastLabel(values.name)} updated successfully!`);
                        resetForm({ values });
                        if (leaveAfterSaveRef.current) return;
                        navigate("/locations");
                    } catch {
                        saveOutcomeRef.current = "failure";
                        showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ isSubmitting, values, submitCount, handleSubmit }) => {
                    const saveDisabled = metaDeleted;

                    const form = (
                        <Form
                            ref={formAutoFocusRef}
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            {metaDeleted && (
                                <Alert variant="warning" className="mb-3">
                                    This location is deleted. Restore it before editing its details.
                                </Alert>
                            )}

                            <LocationEditFormGrid
                                mapConfig={mapConfig}
                                mapsEnabled={mapsEnabled}
                                metaDeleted={metaDeleted}
                                isNew={false}
                                liveValidationAfterSubmit={liveValidationAfterSubmit}
                                submitCount={submitCount}
                            />

                            <EditFormSplitFooter
                                start={
                                    shouldShowEditFormLifecycleActions(false, values.id, isNarrowScreen) ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            <EditFormLifecycleActions
                                                metaDeleted={metaDeleted}
                                                metaEnabled={metaEnabled}
                                                onAction={setConfirmKind}
                                            />
                                        </div>
                                    ) : undefined
                                }
                            >
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={saveDisabled}
                                    readOnlyForm={metaDeleted}
                                    isNewRecord={false}
                                    newRecordLabel={EntityLabels.location}
                                    enableSaveBeforeLeave
                                    leaveAfterSaveRef={leaveAfterSaveRef}
                                    saveOutcomeRef={saveOutcomeRef}
                                    saveLabel="Save"
                                    onCancel={() => navigate("/locations")}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );

                    return (
                        <FormikUnsavedChangesGuard
                            enableRouteBlocking
                            readOnlyForm={metaDeleted}
                            isNewRecord={false}
                            newRecordLabel={EntityLabels.location}
                            enableSaveBeforeLeave
                            leaveAfterSaveRef={leaveAfterSaveRef}
                            saveOutcomeRef={saveOutcomeRef}
                        >
                            {form}
                        </FormikUnsavedChangesGuard>
                    );
                }}
            </Formik>
        </>
    );
};

export { LocationEditFormView };
